// Module-level step-up store (IMP-045) — same tiny pattern as lib/confirm.ts.
//
// When a gated callable returns `step-up-required`, the callable wrapper calls
// requestStepUp(); a single <StepUpRoot /> (mounted in AppShell) renders the
// modal, runs the WebAuthn ceremony, and settles the promise true (verified) or
// false (cancelled/failed). The wrapper then retries the original call once.

type Listener = (active: boolean) => void;

interface StepUpRequest {
  id: string;
  resolve: (verified: boolean) => void;
}

let current: StepUpRequest | null = null;
const listeners = new Set<Listener>();
let nextId = 0;

function emit(): void {
  for (const listener of listeners) listener(current !== null);
}

/** Subscribe to whether a step-up is being requested. Returns unsubscribe. */
export function subscribeStepUp(listener: Listener): () => void {
  listeners.add(listener);
  listener(current !== null);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Request an interactive step-up. Resolves true once the user verifies, false
 * if they cancel or it fails. Last call wins (a prior pending request is
 * cancelled so its awaiter doesn't hang).
 */
export function requestStepUp(): Promise<boolean> {
  if (current) current.resolve(false);
  return new Promise<boolean>((resolve) => {
    current = { id: `stepup-${(nextId += 1)}`, resolve };
    emit();
  });
}

/** Settle the active request and clear it. Called by <StepUpRoot />. */
export function settleStepUp(verified: boolean): void {
  if (current) {
    current.resolve(verified);
    current = null;
    emit();
  }
}
